'use client';

import { Container, Tabs } from '@/components';
import { Content } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { Simplify } from '../../../../prismicio-types';

import { useVisible } from '@/hooks';
import { AnimationItem, ProductsInformationTextItem } from '.';

import styles from './productsInformationInner.module.scss';

interface ProductsInformationInnerProps {
  data: Simplify<Content.ProductsInformationSliceDefaultPrimary>;
}
const animations = [
  '/static/animations/productsInfo/animation-1.json',
  '/static/animations/productsInfo/animation-2.json',
  '/static/animations/productsInfo/animation-3.json',
  '/static/animations/productsInfo/animation-4.json',
];

const components: JSXMapSerializer = {
  heading2: ({ children }) => <h2 className={styles.title}>{children}</h2>,
};

const fetchAnimation = async (path: string) => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch animation: ${path}`);
  }
  return response.json();
};

export const ProductsInformationInner = ({
  data,
}: ProductsInformationInnerProps) => {
  const [currentItem, setCurrentItem] = useState<number>(0);
  const [isVisible, id] = useVisible();
  const [animationList, setAnimationList] = useState<object[]>([]);

  const isVisibleStyles = isVisible ? styles.isVisible : '';

  const params = useSearchParams().get('activeTab');

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const fetchedVideos = await Promise.all(
          animations.map((path) => fetchAnimation(path))
        );
        setAnimationList(fetchedVideos);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching animations:', error);
      }
    };

    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animations]);

  useEffect(() => {
    if (params) {
      const timeoutId = setTimeout(() => setCurrentItem(+params), 500);
      return () => clearTimeout(timeoutId);
    }
  }, [params]);

  return (
    <Container id={id} styles={styles.container}>
      <div className={`${styles.title_wrapper} ${isVisibleStyles}`}>
        <PrismicRichText field={data.title} components={components} />
      </div>
      <Tabs
        activeItem={currentItem}
        onClick={setCurrentItem}
        tabs={data.products.map((item) => item.tab_text)}
        isVisible={isVisible}
        styles={styles.tabs}
        scrollDirection="bottom"
        theme="dark"
      />
      <article className={styles.content}>
        <ul className={styles.text_list}>
          {data.products.map((item, index) => (
            <ProductsInformationTextItem
              isActive={index === currentItem}
              item={item}
              key={item.subtitle}
              isVisible={isVisible}
            />
          ))}
        </ul>
        <ul className={`${styles.animations} ${isVisibleStyles}`}>
          {animationList.map((animation, index) => (
            <AnimationItem
              animation={animation}
              key={index}
              isActive={index === currentItem}
              play={isVisible}
            />
          ))}
        </ul>
      </article>
    </Container>
  );
};
